import { motion } from "framer-motion";

export default function About() {
  const timeline = [
    {
      year: "2024",
      month:"Sept",
      title: "Discovered Coding",
      description:
        "Started experimenting with C and simple programs. Fascinated by how logic could solve problems.",
    },
    {
      year: "2025",
      month:"Jan",
      title: "Web Development",
      description:
        "Dove into HTML, CSS, and JavaScript. Built small web apps.",
    },
    {
      year: "2025",
      month:"July",
      title: "Competitive Programming",
      description:
        "Took part in coding contests. Learned the importance of persistence and problem-solving speed.",
    },
    {
      year: "2024",
      month:"Aug",
      title: "Full-Stack Development",
      description:
        "Exploring React, Django, and backend integrations. Building impactful projects with real-world use.",
    },
  ];

  return (
    <section
      id="about"
      className="relative text-white py-16 md:py-24 pb-12 md:pb-16 px-4 sm:px-6 overflow-hidden"
    >
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan to-neon-purple">
              About Me
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-neon-pink to-neon-purple mx-auto rounded-full"></div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
        className="relative max-w-6xl mx-auto z-10 "
      >
        <div
          className="grid md:grid-cols-[5fr_3fr] gap-8 md:gap-12 items-start px-4 sm:px-6 md:px-8 py-6 md:py-10 
            bg-white/5 backdrop-blur-md border border-white/20 
            rounded-2xl shadow-lg"
        >
          {/* Left Section - Personal Details */}
          <div className="flex flex-col items-center justify-center">
            <h3 className="text-2xl md:text-4xl font-bold text-white mb-4 md:mb-6 text-center md:text-left">
              Behind The Code
            </h3>

            <p className="text-base md:text-lg text-gray-300 mb-4 leading-relaxed">
              My journey into coding began with curiosity — the idea that you
              could create something meaningful out of just a few lines of code
              hooked me instantly. Over time, that curiosity turned into passion
              as I explored how technology can shape our future.
            </p>

            <p className="text-base md:text-lg text-gray-300 mb-4 leading-relaxed">
              Beyond coding, I enjoy{" "}
              <span className="text-neon-green font-medium">
                reading tech blogs, and solving puzzles
              </span>
              . These hobbies fuel my creativity and problem-solving mindset.
            </p>

            <p className="text-base md:text-lg text-gray-300 mb-6 md:mb-8 leading-relaxed">
              One thing that defines me is my{" "}
              <span className="text-neon-purple font-medium">
                unwillingness to give up
              </span>
              . Whether it's debugging stubborn code or learning a tough
              concept, I believe persistence always pays off.
            </p>
          </div>

          {/* Right Section - Enhanced Timeline */}
          <div className="relative overflow-hidden">
            <h3 className="text-2xl font-medium md:text-4xl  text-white mb-4 md:mb-6 text-center md:text-left">
              My Journey
            </h3>

<div className="relative max-h-[300px] md:max-h-[350px] overflow-y-auto overflow-x-hidden pr-2 md:pr-4 hide-scrollbar">
  {/* Animated vertical timeline line */}
  <motion.div 
    initial={{ scaleY: 0 }}
    whileInView={{ scaleY: 1 }}
    transition={{ duration: 1, delay: 0.2 }}
    viewport={{ once: true }}
    style={{ height: `${timeline.length * 200}px` }} 
    className="absolute left-3 md:left-4 top-2 bottom-0 w-0.5 md:w-1 bg-gradient-to-b from-neon-cyan via-neon-purple to-transparent origin-top"
  ></motion.div>
  
  <div className="space-y-6 md:space-y-8 scrollbar-none">
    {timeline.map((item, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, x: 30 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: index * 0.15 }}
        viewport={{ once: true }}
        whileHover={{ scale: 1.02 }}
        className="relative pl-10 md:pl-12"
      >
        {/* Animated timeline dot */}
        <motion.div 
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          transition={{ duration: 0.4, delay: index * 0.2 }}
          viewport={{ once: true }}
          className="absolute left-2 md:left-3 top-1 w-3 h-3 md:w-4 md:h-4 rounded-full bg-gradient-to-r from-neon-cyan to-neon-purple shadow-lg shadow-cyan-500/25 z-10"
        ></motion.div>
        
        {/* Enhanced glassmorphism card */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/25 rounded-xl p-4 md:p-5 hover:bg-white/15 transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/15 group ">
          <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3 ">
            <span className="text-neon-white font-bold text-xs md:text-sm px-2 py-1 md:px-3 md:py-1 rounded-full bg-white/10 border border-cyan-500/30">
              {item.year} {item.month}
            </span>
          </div>
          <span className="text-neon-yellow font-semibold text-base md:text-lg group-hover:text-cyan-300 transition-colors">
            {item.title}
          </span>
          <p className="text-gray-300 text-sm md:text-base leading-relaxed group-hover:text-gray-200 transition-colors">
            {item.description}
          </p>
        </div>
      </motion.div>
    ))}
  </div>
</div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}